import React, { useRef, useEffect } from 'react';
import { useContact } from '../../context/ContactContext';
import { useTranscript } from '../../context/TranscriptContext';

// Phase 3 (2026-09-14): the panel now renders the live Contact Lens feed from
// TranscriptContext (voice, ~3 s poll, stops at ACW). Demo mode seeds the
// same context from the mock contact, so `display.transcript` is no longer
// read here. Speaker tags: Cust = CUSTOMER, You = AGENT (Contact Lens roles);
// AI only ever appears on mock lines — the bot's own prompts are not
// transcribed (Contact Lens transcribes participants, not played prompts).
const SPEAKER_CLASS = { AI: 'ai', Cust: 'cust', You: 'you' };
// v3 (#112): the spec names the speaker above each line. Display only; the
// feed's own speaker values are unchanged.
const SPEAKER_NAME = { AI: 'AI', Cust: 'Caller', You: 'You' };

const STATUS_BADGE = {
  polling:   { cls: '',        text: 'LIVE' },
  waiting:   { cls: 'waiting', text: 'WAITING' },
  stopped:   { cls: 'stopped', text: 'ENDED' },
  demo:      { cls: '',        text: 'DEMO' },
  forbidden: { cls: 'error',   text: 'NO ACCESS' },
  error:     { cls: 'error',   text: 'OFFLINE' },
  idle:      { cls: 'stopped', text: 'OFF' },
};

function emptyText(status, channel, enabled) {
  if (!enabled) return 'Live transcript endpoint not configured (VITE_TRANSCRIPT_URL).';
  if (channel && channel !== 'VOICE') return 'Live transcript is voice-only for now — chat streaming is a separate item.';
  switch (status) {
    case 'waiting': return 'Waiting for the first transcript segment — Contact Lens usually lands the first lines within ~20 s of connect.';
    case 'forbidden': return 'You are not entitled to this contact’s tenant, so its transcript is not shown.';
    case 'error': return 'Transcript feed is unavailable — the console stopped polling after repeated errors.';
    case 'stopped': return 'No transcript was produced for this contact.';
    default: return 'Transcript starts when a voice contact connects.';
  }
}

function highlightEntities(text, entities = []) {
  if (!entities.length) return text;
  const parts = [];
  let remaining = text;
  let key = 0;
  for (const entity of entities) {
    const idx = remaining.indexOf(entity);
    if (idx === -1) continue;
    if (idx > 0) parts.push(remaining.slice(0, idx));
    parts.push(<span key={key++} className="entity-hl">{entity}</span>);
    remaining = remaining.slice(idx + entity.length);
  }
  if (remaining) parts.push(remaining);
  return parts.length ? parts : text;
}

export default function LiveTranscript() {
  const { display } = useContact();
  const { lines, status, enabled } = useTranscript();
  const bottomRef = useRef(null);
  const channel = display?.meta?.channel ?? null;

  useEffect(() => {
    // Nothing to follow when there are no lines — and on mount this scroll
    // could drag the overflow:hidden centre column upward, cropping the
    // top card (Storm's live task check, 2026-09-22).
    if (lines.length === 0) return;
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [lines.length]);

  // Non-voice contacts (tasks, chat): the feed does not apply, and on a task
  // the panel is header-only (oncall-v3.css), so the badge carries the reason.
  const badge = channel && channel !== 'VOICE' ? { cls: 'stopped', text: 'VOICE ONLY' } : (STATUS_BADGE[status] ?? STATUS_BADGE.idle);

  return (
    <div className="transcript-panel">
      <div className="transcript-header">
        <span>Live Transcript</span>
        <span className={`live-badge ${badge.cls}`}>
          <span className="live-dot" />
          {badge.text}
        </span>
      </div>

      <div className="transcript-body">
        {lines.length === 0 ? (
          <p className="panel-empty">{emptyText(status, channel, enabled)}</p>
        ) : (
          lines.map(line => (
            <div key={line.id} className="transcript-line">
              <span className="transcript-time">{line.time}</span>
              <div className="transcript-msg">
                <div className="transcript-who">
                  <span className={`speaker-tag ${SPEAKER_CLASS[line.speaker] ?? 'you'}`}>
                    {SPEAKER_NAME[line.speaker] ?? line.speaker}
                  </span>
                  {line.sentiment && (
                    <span
                      className={`line-sent ${line.sentiment.toLowerCase()}`}
                      title={`${line.sentiment.charAt(0)}${line.sentiment.slice(1).toLowerCase()} (Contact Lens)`}
                    />
                  )}
                </div>
                <span className={`transcript-text ${SPEAKER_CLASS[line.speaker] ?? 'you'}`}>
                  {highlightEntities(line.text, line.entities)}
                </span>
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}
